import { useState, useEffect, useCallback } from 'react';

const CACHE_KEY_PREFIX = 'pineTracker_userData_';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

export const useCachedUserData = (userId) => {
  const [cachedData, setCachedData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const getCacheKey = useCallback((uid) => {
    return `${CACHE_KEY_PREFIX}${uid}`;
  }, []);

  const getCachedData = useCallback((uid) => {
    try {
      const cacheKey = getCacheKey(uid);
      const cached = localStorage.getItem(cacheKey);
      
      if (!cached) return null;
      
      const parsed = JSON.parse(cached);
      
      // Check if cache is still valid
      const now = Date.now();
      if (now - parsed.timestamp > CACHE_DURATION) {
        localStorage.removeItem(cacheKey);
        return null;
      }
      
      return parsed.data;
    } catch (err) {
      console.error('Error reading cache:', err);
      return null;
    }
  }, [getCacheKey]);

  const setCachedDataForUser = useCallback((uid, data) => {
    try {
      const cacheKey = getCacheKey(uid);
      const cacheItem = {
        data,
        timestamp: Date.now(),
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
    } catch (err) {
      console.error('Error writing cache:', err);
    }
  }, [getCacheKey]);

  const clearCacheForUser = useCallback((uid) => {
    try {
      const cacheKey = getCacheKey(uid);
      localStorage.removeItem(cacheKey);
    } catch (err) {
      console.error('Error clearing cache:', err);
    }
  }, [getCacheKey]);

  const clearAllCache = useCallback(() => {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(CACHE_KEY_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (err) {
      console.error('Error clearing all cache:', err);
    }
  }, []);

  const refreshData = useCallback(async (uid, fetchFunction, onProgress) => {
    setIsLoading(true);
    setError(null);
    
    try {
      onProgress?.(10, 'Starting data fetch...');
      
      const data = await fetchFunction(uid);
      
      onProgress?.(50, 'Processing user data...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      onProgress?.(80, 'Caching results...');
      setCachedDataForUser(uid, data);
      
      onProgress?.(100, 'Data ready!');
      setCachedData(data);
      
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [setCachedDataForUser]);

  // Load cached data on mount if userId provided
  useEffect(() => {
    if (userId) {
      const cached = getCachedData(userId);
      setCachedData(cached);
    }
  }, [userId, getCachedData]);

  return {
    cachedData,
    isLoading,
    error,
    getCachedData,
    setCachedDataForUser,
    clearCacheForUser,
    clearAllCache,
    refreshData,
    hasValidCache: cachedData !== null,
  };
};